/**
 * PURPOSE: Elegir el carril al agregar a una serie de pista (ventanas "Agregar deportista" y "Agregar institución").
 * RESPONSIBILITIES: Grilla de carriles 1-12 con quién ocupa cada uno (800/1500: caben dos). Si el carril elegido está lleno
 *   ofrece "Reemplazar a X" (X sale de la serie y vuelve a inscritos sin serie) o "Intercambiar" (X queda en la serie sin
 *   carril), a quién si hay dos y la confirmación si X ya tiene marca o estado. Así "quitar a UTEC del carril 6 y poner a
 *   UC-HU" es una sola acción. La regla vive en placement.resolveLaneChoice y el servidor la repite.
 * COLLABORATORS: AddAthleteModal.jsx, AddTeamModal.jsx, placement.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/LanePicker.jsx
 */
import React from "react";
import { FiAlertTriangle } from "react-icons/fi";

import {
  LANE_NUMBERS,
  describeEntryResult,
  isLaneFull,
  laneCapacity,
  occupantLabel,
  occupantsOfLane,
  resolveLaneChoice,
  roundName,
} from "../placement.js";

export const EMPTY_LANE_CHOICE = Object.freeze({ mode: null, targetRowId: null, confirmResult: false });

const LanePicker = ({ prueba, round, lane, onLaneChange, choice = EMPTY_LANE_CHOICE, onChoiceChange, inCombined = false }) => {
  const picked = Number(lane) || null;
  const resolved = resolveLaneChoice({ prueba, round, lane: picked, choice, inCombined });
  const setChoice = (patch) => onChoiceChange({ ...choice, ...patch });
  const target = resolved.target;
  const targetName = target ? occupantLabel(target) : "el ocupante";

  return (
    <div className="space-y-2">
      <fieldset>
        <legend className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
          Carril{laneCapacity(prueba) > 1 ? " (800/1500: caben dos por carril)" : ""}
        </legend>
        <div role="radiogroup" aria-label="Carril" className="mt-1 grid grid-cols-2 gap-1 sm:grid-cols-3">
          {LANE_NUMBERS.map((number) => {
            const occupants = occupantsOfLane(round, number);
            const full = isLaneFull(prueba, round, number);
            const active = picked === number;
            return (
              <button
                key={number}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  onLaneChange(String(number));
                  onChoiceChange(EMPTY_LANE_CHOICE);
                }}
                title={occupants.length ? occupants.map(occupantLabel).join(" · ") : "Libre"}
                className={`rounded-lg border px-2 py-1 text-left text-xs transition ${
                  active
                    ? "border-indigo-500 bg-indigo-50 font-bold text-indigo-800"
                    : full
                      ? "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="font-black">Carril {number}</span>
                <span className="block truncate text-[11px] text-slate-500">
                  {occupants.length ? `${full ? "Ocupado" : "1 de 2"}: ${occupants.map(occupantLabel).join(" · ")}` : "Libre"}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {resolved.full ? (
        <div role="group" aria-label="Carril ocupado" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="flex items-start gap-2 font-bold">
            <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" />
            El carril {picked} de {roundName(round)} está ocupado por {resolved.occupants.map(occupantLabel).join(" y ")}.
          </p>
          {resolved.occupants.length > 1 ? (
            <div role="radiogroup" aria-label="¿A quién?" className="mt-2 space-y-1">
              {resolved.occupants.map((item) => (
                <label key={item.id} className="flex items-center gap-2 font-semibold">
                  <input
                    type="radio"
                    name="lane-target"
                    checked={choice.targetRowId === item.id}
                    onChange={() => setChoice({ targetRowId: item.id, confirmResult: false })}
                  />
                  {occupantLabel(item)}
                </label>
              ))}
            </div>
          ) : null}
          <div role="radiogroup" aria-label="Qué hacer con el ocupante" className="mt-2 space-y-1">
            <label className="flex items-start gap-2 font-semibold">
              <input type="radio" name="lane-mode" className="mt-0.5" checked={choice.mode === "replace"} onChange={() => setChoice({ mode: "replace" })} />
              <span>
                Reemplazar a {targetName}: sale de la serie (vuelve a inscritos sin serie).
              </span>
            </label>
            <label className="flex items-start gap-2 font-semibold">
              <input type="radio" name="lane-mode" className="mt-0.5" checked={choice.mode === "swap"} onChange={() => setChoice({ mode: "swap" })} />
              <span>Intercambiar: {targetName} queda en esta serie sin carril (asígnale uno después).</span>
            </label>
          </div>
          {resolved.withResult ? (
            <label className="mt-2 flex items-start gap-2 font-bold">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(choice.confirmResult)}
                onChange={(event) => setChoice({ confirmResult: event.target.checked })}
              />
              <span>
                {targetName} ya tiene {describeEntryResult(target)}:{" "}
                {choice.mode === "swap" ? "queda en la serie sin carril, con su resultado." : "se pierde en esta serie."} Lo confirmo.
              </span>
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default LanePicker;
